import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  HStack,
  Badge,
  Text,
  useToast,
  Spinner,
  Flex,
  Stack,
  Divider,
  useBreakpointValue
} from '@chakra-ui/react';
import { apiService } from '../services/apiService';

export default function EnrollmentValidation() {
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);

  const fetchPending = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getPendingEnrollments();
      setEnrollments(data);
    } catch (err) {
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les demandes',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const handleApprove = async (id) => {
    try {
      setActionId(id);
      await apiService.approveEnrollment(id);
      toast({
        title: 'Adhésion validée',
        description: 'Le membre a été activé et un email de confirmation lui a été envoyé.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      await fetchPending();
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setActionId(null);
    }
  };

  const handleReject = async (id) => {
    try {
      setActionId(id);
      await apiService.rejectEnrollment(id);
      toast({
        title: 'Demande rejetée',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      await fetchPending();
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setActionId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  const renderBoutons = (e) => (
    <HStack spacing={2}>
      <Button
        size="sm"
        colorScheme="green"
        onClick={() => handleApprove(e.id)}
        isLoading={actionId === e.id}
        isDisabled={actionId !== null && actionId !== e.id}
      >
        Valider
      </Button>
      <Button
        size="sm"
        colorScheme="red"
        variant="outline"
        onClick={() => handleReject(e.id)}
        isLoading={actionId === e.id}
        isDisabled={actionId !== null && actionId !== e.id}
      >
        Rejeter
      </Button>
    </HStack>
  );

  return (
    <Box maxW="1100px" mx="auto">
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">Demandes d'adhésion en attente</Heading>
        <Badge colorScheme={enrollments.length > 0 ? 'orange' : 'green'} fontSize="md" px={3} py={1} borderRadius="md">
          {enrollments.length} demande{enrollments.length !== 1 ? 's' : ''}
        </Badge>
      </Flex>

      {loading ? (
        <Flex justify="center" py={10}><Spinner size="xl" /></Flex>
      ) : enrollments.length === 0 ? (
        <Box bg="white" p={8} borderRadius="md" shadow="sm" textAlign="center">
          <Text color="gray.500">Aucune demande d'adhésion en attente.</Text>
        </Box>
      ) : isMobile ? (
        <Stack spacing={3} divider={<Divider />} bg="white" p={4} borderRadius="md" shadow="sm">
          {enrollments.map((e) => (
            <Box key={e.id}>
              <Text fontWeight="semibold">{e.prenom} {e.nom}</Text>
              <Text fontSize="sm" color="gray.600">{e.email || '—'}</Text>
              <Text fontSize="sm" color="gray.600">{e.telephone || '—'}</Text>
              <Text fontSize="sm" color="gray.500">
                Né(e) le {formatDate(e.date_naissance)}{e.lieu_naissance ? ' à ' + e.lieu_naissance : ''}
              </Text>
              <Text fontSize="xs" color="gray.400">Demande du {formatDate(e.created_at)}</Text>
              <Box mt={2}>{renderBoutons(e)}</Box>
            </Box>
          ))}
        </Stack>
      ) : (
        <Box bg="white" borderRadius="md" shadow="sm" overflowX="auto">
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Nom / Prénom</Th>
                <Th>Email</Th>
                <Th>Téléphone</Th>
                <Th>Date de naissance</Th>
                <Th>Lieu de naissance</Th>
                <Th>Soumis le</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {enrollments.map((e) => (
                <Tr key={e.id}>
                  <Td fontWeight="semibold">{e.prenom} {e.nom}</Td>
                  <Td>{e.email || '—'}</Td>
                  <Td>{e.telephone || '—'}</Td>
                  <Td>{formatDate(e.date_naissance)}</Td>
                  <Td>{e.lieu_naissance}</Td>
                  <Td>{formatDate(e.created_at)}</Td>
                  <Td>
                    {renderBoutons(e)}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
